import React, {
    useEffect,
    useContext,
    FormEvent,
    useState,
    useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { homeStore } from "../../../contexts/HomeContext";
import { sessionStore } from "../../../contexts/SessionContext";
import { configStore } from "../../../contexts/ConfigContext";
import { User, UserType } from "../../../interfaces/User";
import Content from "../Content/Content/Content";
import "./Home.scss";
import { IHomeContent } from "../../../interfaces/Home";
import useAthenaAPIClient from "../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import { FormattedMessage, useIntl } from "react-intl";
import htmlHandler from "../../../utils/html-handler";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import { Image } from "@evidenceb/athena-common/modules/Assets";
import { parse } from "@evidenceb/athena-common/modules/HTMLParser";
import { useFeatureFlags } from "@evidenceb/athena-common/modules/FeatureFlags";
import useAssetsDetails from "../../../hooks/useAssetsDetails";
import LandingPage from "../LandingPage";

interface IUser {
    first_name: string;
    last_name: string;
}

function Home() {
    const { home } = useContext(homeStore);
    const { pathname } = useLocation();
    const { config } = useContext(configStore);
    const { session, setSession } = useContext(sessionStore);
    const [state, setState] = useState<IUser>({
        first_name: "",
        last_name: "",
    });
    const flags = useFeatureFlags();

    const [displayError, setDisplayError] = useState<boolean>();
    const [displaySignIn, setDisplaySignIn] = useState<boolean>();
    const [displayHomeContent, setDisplayHomeContent] = useState<boolean>(
        !session.flags.displaySignIn
    );
    const [status, setStatus] = useState<"idle" | "saving">("idle");
    const athenaAPIClient = useAthenaAPIClient();
    const intl = useIntl();
    const assetsDetails = useAssetsDetails();

    const signInChangeHandler = (e: FormEvent<HTMLInputElement>) => {
        setState({
            ...state!,
            [e.currentTarget.name]: e.currentTarget.value,
        });
        if (displayError) {
            setDisplayError(false);
        }
    };

    const signInHandler = async () => {
        setStatus("saving");
        const user: User = await athenaAPIClient.getTeacher(session.userId);
        if (user) {
            let userBody = {
                first_name: state?.first_name,
                last_name: state?.last_name,
            };

            const userUpdated: User = await athenaAPIClient.updateUser(
                "teacher",
                user.id,
                userBody
            );

            if (userUpdated.first_name !== "" && userUpdated.last_name !== "") {
                console.log(`API: PATCH: USER (teachers)`, userUpdated);
                setSession((session) => {
                    return {
                        ...session,
                        flags: {
                            ...session.flags,
                            displaySignIn: false,
                        },
                    };
                });
                setDisplaySignIn(false);
                setDisplayHomeContent(true);
            }
        }
        setStatus("idle");
    };

    const getUserData = useCallback(async () => {
        const user: User = await athenaAPIClient.getTeacher(session.userId);
        return user;
    }, [athenaAPIClient, session.userId]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const showSignIn = session.flags.displaySignIn;
        if (showSignIn) {
            getUserData().then((user) => {
                setState({
                    first_name: user.first_name,
                    last_name: user.last_name,
                });
            });
            setDisplaySignIn(showSignIn);
        }
    }, [getUserData, session.flags.displaySignIn]);

    const signIn = (
        <div className="signIn-container">
            <div className="block left">
                <Image
                    assetsDetails={assetsDetails}
                    src={config.logos.header_client}
                />
                <div className="signIn">
                    <div className="header">
                        <h1>
                            <FormattedMessage
                                id="auth-teacher-signIn-title"
                                defaultMessage="Welcome to {appName}"
                                values={{ appName: config.client_name }}
                            />
                        </h1>
                        <p>
                            {parse(
                                intl.formatMessage(
                                    {
                                        id: "html-auth-teacher-signIn-text",
                                        defaultMessage:
                                            "To start learning, enter your name",
                                    },
                                    htmlHandler
                                )
                            )}
                        </p>
                    </div>
                    <div className="form">
                        <input
                            type="text"
                            placeholder={intl.formatMessage({
                                id: "auth-form-name",
                                defaultMessage: "Last name",
                            })}
                            name="last_name"
                            value={state?.last_name}
                            onChange={signInChangeHandler}
                        />
                        <input
                            type="text"
                            placeholder={intl.formatMessage({
                                id: "auth-form-firstname",
                                defaultMessage: "First name",
                            })}
                            name="first_name"
                            value={state?.first_name}
                            onChange={signInChangeHandler}
                        />
                        <Button
                            variant="secondary"
                            label={intl.formatMessage({
                                id: "auth-form-button-start",
                                defaultMessage: "Start",
                            })}
                            disabled={
                                state?.last_name === "" ||
                                state?.first_name === "" ||
                                status === "saving"
                            }
                            onClick={signInHandler}
                        />
                    </div>
                </div>
            </div>
            <div className="block right">
                <Image
                    assetsDetails={assetsDetails}
                    src={config.auth?.rightImage}
                />
            </div>
        </div>
    );

    return (
        <>
            <div id="home-teacher">
                {config.auth?.mode === "REGISTER" ||
                config.auth?.mode === "REGISTER_NAMEONLY"
                    ? displaySignIn
                        ? signIn
                        : null
                    : null}
                {displayHomeContent ? (
                    flags.adsecNavigation ? (
                        <LandingPage />
                    ) : (
                        <Content
                            content={
                                session.userType === UserType.Teacher
                                    ? home.teacher
                                    : (home.student as IHomeContent)
                            }
                        />
                    )
                ) : null}
            </div>
        </>
    );
}

export default Home;
